import type {
  CanvasBackground,
  CanvasLayout,
  CanvasSettings,
  CanvasTheme,
  GridSettings,
  ResponsiveSettings,
} from "../../types";
import { defaultCanvasSettings, typedObjectKeys } from "./editor-store-helpers";
import type { HistorySlice } from "./history-slice";
import type {
  EditorImmerGetState,
  EditorImmerSetState,
} from "./editor-store-types";
import { VersionIntegrationContext } from "./versioning-integration";

export interface CanvasSlice {
  canvasSettings: CanvasSettings;
  updateCanvasSettings: (updates: Partial<CanvasSettings>) => void;
  updateCanvasBackground: (background: Partial<CanvasBackground>) => void;
  updateGridSettings: (grid: Partial<GridSettings>) => void;
  updateCanvasLayout: (layout: Partial<CanvasLayout>) => void;
  updateCanvasTheme: (theme: Partial<CanvasTheme>) => void;
  updateResponsiveSettings: (responsive: Partial<ResponsiveSettings>) => void;
  resetCanvasSettings: () => void;
}

export const canvasInitialState: Pick<CanvasSlice, "canvasSettings"> = {
  canvasSettings: defaultCanvasSettings,
};

type CanvasSliceState = CanvasSlice &
  Pick<HistorySlice, "pushToHistory"> & {
    versioningEnabled: boolean;
    gridEnabled: boolean;
    gridSize: number;
    snapToGrid: boolean;
    isModified: boolean;
  };

export function createCanvasSlice<TState extends CanvasSliceState>(
  set: EditorImmerSetState<TState>,
  get: EditorImmerGetState<TState>,
): CanvasSlice {
  return {
    ...canvasInitialState,

    updateCanvasSettings: (updates) => {
      const oldSettings = get().canvasSettings;

      if (get().versioningEnabled) {
        const versionContext = VersionIntegrationContext.getInstance();
        typedObjectKeys(updates).forEach((key) => {
          const fieldName = String(key);
          versionContext.trackCanvasChange(
            "settingsUpdate",
            "canvas",
            oldSettings[key],
            updates[key],
            {
              field: fieldName,
              description: `Updated canvas ${fieldName}`,
            },
          );
        });
      }

      set((state) => {
        state.canvasSettings = {
          ...state.canvasSettings,
          ...updates,
        };
        state.isModified = true;
      });

      get().pushToHistory();
    },

    updateCanvasBackground: (background) => {
      const oldBackground = get().canvasSettings.background;

      if (get().versioningEnabled) {
        VersionIntegrationContext.getInstance().trackCanvasChange(
          "backgroundUpdate",
          "canvas-background",
          oldBackground,
          { ...oldBackground, ...background },
          { description: "Updated canvas background" },
        );
      }

      set((state) => {
        state.canvasSettings.background = {
          ...state.canvasSettings.background,
          ...background,
        };
        state.isModified = true;
      });

      get().pushToHistory();
    },

    updateGridSettings: (grid) => {
      set((state) => {
        state.canvasSettings.grid = {
          ...state.canvasSettings.grid,
          ...grid,
        };

        if (grid.enabled !== undefined) {
          state.gridEnabled = grid.enabled;
        }
        if (grid.size !== undefined) {
          state.gridSize = grid.size;
        }
        if (grid.snapToGrid !== undefined) {
          state.snapToGrid = grid.snapToGrid;
        }

        state.isModified = true;
      });

      get().pushToHistory();
    },

    updateCanvasLayout: (layout) => {
      const oldLayout = get().canvasSettings.layout;

      if (get().versioningEnabled) {
        VersionIntegrationContext.getInstance().trackCanvasChange(
          "layoutUpdate",
          "canvas-layout",
          oldLayout,
          { ...oldLayout, ...layout },
          { description: "Updated canvas layout" },
        );
      }

      set((state) => {
        state.canvasSettings.layout = {
          ...state.canvasSettings.layout,
          ...layout,
        };
        state.isModified = true;
      });

      get().pushToHistory();
    },

    updateCanvasTheme: (theme) => {
      const oldTheme = get().canvasSettings.theme;

      if (get().versioningEnabled) {
        VersionIntegrationContext.getInstance().trackCanvasChange(
          "themeUpdate",
          "canvas-theme",
          oldTheme,
          { ...oldTheme, ...theme },
          { description: "Updated canvas theme" },
        );
      }

      set((state) => {
        state.canvasSettings.theme = {
          ...state.canvasSettings.theme,
          ...theme,
        };
        state.isModified = true;
      });

      get().pushToHistory();
    },

    updateResponsiveSettings: (responsive) => {
      set((state) => {
        state.canvasSettings.responsive = {
          ...state.canvasSettings.responsive,
          ...responsive,
        };
        state.isModified = true;
      });

      get().pushToHistory();
    },

    resetCanvasSettings: () => {
      set((state) => {
        state.canvasSettings = { ...defaultCanvasSettings };
        state.gridEnabled = defaultCanvasSettings.grid.enabled;
        state.gridSize = defaultCanvasSettings.grid.size;
        state.snapToGrid = defaultCanvasSettings.grid.snapToGrid;
        state.isModified = true;
      });

      get().pushToHistory();
    },
  };
}
